import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Form, Steps } from "antd";
import { validateNotEmpty } from "../../../../validator/antdValidator";
import "./CreateTrait.css";
import TraitCondition from "../components/TraitCondition/TraitCondition";
import { t } from "i18next";
import CustomBreadCrumb from "components/CustomBreadCrumb/CustomBreadCrumb";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import Selector from "components/Selector/Selector";
import centralApi from "services/centralApi";
import { useDispatch, useSelector } from "react-redux";
import useMessage from "hooks/useMessage";
import {
  addFilterCondition,
  fetchComputedTraitById,
  formatComputedTraitFilterInfo,
  removeFilterCondition,
  resetQueryPayload,
  restructureFilterConditions,
  setQueryPayloadData,
  setFilterInfoData,
} from "redux/features/audience/queryPayloadSlice";
import { STATUS } from "redux/constant";
import Spinner from "components/Spinner/Spinner";
import ComputedTrait from "components/ComputedTrait/ComputedTrait";
import { API_ENDPOINTS, ROUTES } from "utils/constants";
import SegmentBlockLayout from "layout/SegmentBlockLayout/SegmentBlockLayout";
import { getObjectId } from "services/getObjectId";
import {
  AudienceConditionTagEnum,
  AudienceManagementPageType,
  DBPlatformEnum,
  E_OperatorEnum,
  OperatorTypeEnum
} from "utils/enums";
import SegmentedCustom from "components/SegmentedCustom/SegmentedCustom";
import {
  FilterInfo,
  ProjectionInfo,
  QueryPayload,
  RowData,
} from "utils/models";
import { eOperatorType } from "@customTypes/audience";
import {
  E_OPERATOR_OPTIONS,
  PAGE_OPTIONS,
} from "utils/constants/selectorOptions";
import { getToken } from "redux/features/app/authTokenSlice";
import { OperatorManager } from "utils/operators";
import { CreateHeader } from "pages/Audience/AudienceCreate/components/CreateHeader/CreateHeader";
import PageLayout from "layout/PageLayout/PageLayout";
import CustomTable from "components/CustomTable/CustomTable";
import AccessControl from "components/HOC/AccessControl/AccessControl";
import {
  FeatureSettingsFlagTypes,
} from "utils/constants/constants";
import FilterPopup from "components/PopUp/FilterPopup/FilterPopup";
import { audienceItems } from "helperFunctions/audience";
import EventCondition from "pages/Funnels/CreateFunnel/components/EventCondition/EventCondition";
import { fetchComputedTraitList } from "redux/features/audience/computedTraitListSlice";
import { fetchGeoFencingList } from "redux/features/audience/geoFencingListSlice";
/**
 * CreateTrait Component
 *
 * This component is used to create or update a trait . It provides a form interface
 * for defining trait details, including setting conditions and selecting relevant options. It interacts
 * with various Redux actions and API endpoints to fetch, submit, and manage trait data.
 *
 * @returns {JSX.Element} - The rendered component.
 */
function CreateTrait({ segment }: { segment: boolean }) {
  const navigate = useNavigate();
  const features = useSelector((state: any) => state.featuresSetting.data);
  const v2Disabled = features?.[0]?.features?.[FeatureSettingsFlagTypes.AUDIENCE_V2];
  const { showError, showSuccess } = useMessage();
  const [metaData, setMetaData] = useState<any>({});
  const { data: traitList, status: traitListStatus } = useSelector(
    (state: any) => state.computedTraitList
  );
  const [form] = Form.useForm();
  const [loading, setLoading] = useState({
    submit: false,
    columns: false,
    previewResult: false,
    count: false,
    export:false
  });
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const { data: trait, status: traitStatus } = useSelector(
    (state: any) => state.queryPayload
  );
  const [previewResult, setPreviewResult] = useState({
    columns: [],
    data: [],
    limit: 1000,
  });
  const { appId } = useSelector((state: any) => state.activeApp);
  const dispatch: any = useDispatch();
  const { state } = useLocation();
  // Memoized value for form validation
  const isFormValidForPreview = useMemo(() => {
    return (trait.tableInfo?.tableName && trait?.filterInfo.length) || false;
  }, [trait]);
 /**
 * Retrieves the platform(s) from the given filter conditions.
 *
 * This function searches through the `filterInfo` array and its immediate `group` children
 * to find the first condition with the operand `"p"`, which represents a platform condition.
 *
 * If such a condition is found and its value contains only valid platforms,
 * it returns the value (an array of `DBPlatformEnum`).
 *
 * If not found or invalid, and `segment` is true, it returns all supported platforms as fallback.
 * If neither condition is met, it returns `undefined`.
 *
 * @param filterInfo - An array of filter conditions which may include nested `group` conditions.
 * @param segment - A flag indicating whether to return all platforms as a fallback.
 * @returns The selected platform(s) if found and valid,
 *          otherwise `[DBPlatformEnum.ANDROID, DBPlatformEnum.IOS, DBPlatformEnum.WEB]` if `segment` is true,
 *          or `undefined`.
 */
const getPlatform = (
  filterInfo: FilterInfo[]
): DBPlatformEnum[] | undefined => {
  const allPlatforms = [DBPlatformEnum.ANDROID, DBPlatformEnum.IOS, DBPlatformEnum.WEB];

  // Flatten top-level and nested group conditions into a single array
  const allConditions = filterInfo.flatMap(condition =>
    [condition, ...(condition.group ?? [])]
  );

  // Find the first condition with operand "p" (platform)
  const platformCondition = allConditions.find(cond => cond.operand === "p");

  // Validate and return the platform value if all items are valid platforms
  if (platformCondition?.value?.every((item: any) => allPlatforms.includes(item))) {
    return platformCondition.value;
  }

  // If not found or invalid, return all platforms if segment is true
  return segment ? allPlatforms : undefined;
};

  // Handle form submission to save or update the trait
  const onFormSubmit = async () => {
    try {
      // Set loading state to true while the API request is being processed
      setLoading((prevState) => ({ ...prevState, submit: true }));
      //call api only  if the id not found in redux
      const audid =
        trait?.audid || (await getObjectId(appId, loginUser.api_key));
      // Prepare parameters for the API request, including user API key and app ID
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        segment,
        v2:v2Disabled==undefined?segment:!v2Disabled,
        id: state?.id, // Optional ID for updating an existing trait
      };

      // format filter  info
      let filterInfo: FilterInfo[] = formatComputedTraitFilterInfo(
        trait.filterInfo
      );
      filterInfo = restructureFilterConditions(filterInfo);
      // Create a new TraitsPayload object with the necessary data
      const traitsPayload = new QueryPayload({ ...trait, filterInfo, audid });
      const authToken = await dispatch(getToken()).unwrap();
      // Combine parameters with the traits payload to form the final request payload
      const payloadWithParams = {
        ...params,
        data: new QueryPayload({ ...traitsPayload,p:getPlatform(traitsPayload.filterInfo) }).toString(),
        authToken,
      };
      // Call the API to either update an existing trait or create a new one
      const res = await centralApi(
        "POST",
        state?.id
          ? API_ENDPOINTS.UPDATE_CTRAIT_URL // URL for updating an existing trait
          : API_ENDPOINTS.CREATE_CTRAIT_URL, // URL for creating a new trait
        payloadWithParams,
        params
      );
      // Check if the API request was acknowledged successfully
      if (res.acknowledged) {
        // Show success message based on whether it's an update or create operation
        showSuccess(
          !state?.id
            ? t(
                segment
                  ? "segmentCreatedSuccessfullyMsg"
                  : "traitSavedSuccessMsg"
              )
            : t(
                segment
                  ? "segmentUpdatedSuccessfullyMsg"
                  : "traitUpdatedSuccessMsg"
              )
        );
      }
    } catch (error) {
      // Show error message if something goes wrong during the API call
      showError(t("somethingWrongLabel"));
    } finally {
      dispatch(getToken());
      // Reset loading state and navigate to the all traits page
      setLoading((prevState) => ({ ...prevState, submit: false }));
      navigate(
        segment
          ? ROUTES.DASHBOARD_AUDIENCE
          : ROUTES.DASHBOARD_AUDIENCE_ALLTRAITS
      );
    }
  };

  const fetchTraitMetaData = async () => {
    try {
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
      };
      const data = await centralApi(
        "GET",
        API_ENDPOINTS.TRAIT_META_DATA_URL,
        null,
        params
      );
      setMetaData((prevState: any) => ({ ...prevState, ...data }));
    } catch (error) {}
  };
  /**
   * "fetchTableInfo" Fetches information about a table from  API.
   *
   * @param operand - name of column optional
   *  If both table and column are available, it returns column values.
   *  If only the table name is provided, it returns column names for that table.
   * @returns The data fetched from the API or an empty array in case of an error.
   */
  const fetchTableInfo = async (payload?: RowData) => {
    let projection: ProjectionInfo[] = [];
    if (payload) {
      projection = [
        {
          name: payload?.label,
          operand: payload.value,
          type: payload.type || "",
          jsonb: payload?.jsonb || undefined,
          jsonb_type: payload.jsonb_type || undefined,
          modifierInfo: {
            limit: 50,
          },
        },
      ];
    }
    const queryPayload = new QueryPayload({
      name: "",
      tableInfo: {
        tableName: trait.tableInfo?.tableName,
      },
      projectionInfo: projection,
      filterInfo: [],
      modifierInfo: {
        limit: 1000,
      },
    });
    try {
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        data: queryPayload.toString(),
        referenceTable: payload?.referenceTable,
        segment,
      };
      //call api
      const data = await centralApi(
        "GET",
        API_ENDPOINTS.GET_TRAIT_TABLE_INFO_URL,
        null,
        params
      );
      return data;
    } catch (error) {
      return [];
    }
  };
  /**
   * getColumns function calls the `fetchTableInfo` function to fetch column information for the current table.
   * It then updates the `metaData` state with the fetched columns information.
   */
  const getColumns = async () => {
    setLoading((prevState) => ({ ...prevState, columns: true }));
    const { columns, identityColumns, categorizedData } =
      await fetchTableInfo();
    //set state
    setMetaData((prevState: any) => ({
      ...prevState,
      columns: columns,
      identityColumns,
      categorizedData,
    }));
    setLoading((prevState) => ({ ...prevState, columns: false }));
  };
  const filterPopupItems=useMemo(()=>{
   return segment?audienceItems.getfilterPopupData(traitList,trait):[]
  },[trait?.filterInfo,traitList,segment])
  /**
   * Generates an array of step items for rendering the list of conditions in the trait form.
   *
   * @returns {Array} - An array of step items where each item represents a condition block or an action button.
   */
  const stepItems = () => {
    return [
      ...(trait?.filterInfo || []).map(
        (condition: FilterInfo , index: number) => ({
          icon:index ? (
            <SegmentedCustom
              value={condition.e_operator}
              onChange={(value: eOperatorType) => {
                dispatch(
                  setFilterInfoData({
                    data: { ...condition, e_operator: value },
                    index,
                    parentIndex: -1,
                  })
                );
              }}
              options={E_OPERATOR_OPTIONS}
            />
          ) : (
            <PrimaryButton className="trait-step-icon-btn" type="primary">
              {trait?.filterInfo?.[1]?.e_operator == E_OperatorEnum.OR
                ? t("anyLabel")
                : t("allLabel")}
            </PrimaryButton>
          ),
          // Title of the step, including the condition details
          title: 
            <SegmentBlockLayout
              onDeleteClick={
                   index == trait.filterInfo?.length - 1
                  ? () => dispatch(removeFilterCondition({ index }))
                  : undefined
              }
            >
              {condition.tag === AudienceConditionTagEnum.LIVE_EVENT ||
              condition.tag === AudienceConditionTagEnum.PAST_EVENT ||
              condition.tag === AudienceConditionTagEnum.JOURNEY_ACTION ? (
                <EventCondition
                  segment
                  form={form}
                  condition={condition}
                  index={index}
                />
              ) : condition.tag === AudienceConditionTagEnum.TRAIT ? (
                <TraitPreview condition={condition} />
              ) : (
                <TraitCondition
                  segment={segment}
                  form={form}
                  key={index}
                  fetchTableInfo={fetchTableInfo}
                  metaData={metaData}
                  condition={condition}
                  index={index}
                ></TraitCondition>
              )}
            </SegmentBlockLayout>
          ,
        })
      ),
      {
        icon: !segment ? (
          <PrimaryButton
            disabled={!trait.tableInfo?.tableName}
            loading={loading.columns}
            onClick={() => dispatch(addFilterCondition({}))}
            type="primary"
          >
            {t("addConditionLabel")}
          </PrimaryButton>
        ) : (
          filterPopupItems && <FilterPopup
            data={filterPopupItems}
            onItemClick={({ condition }) => {
              dispatch(addFilterCondition({ data: condition || {} }));
            }}
          >
            <PrimaryButton type="primary">
              {t("addConditionLabel")}
            </PrimaryButton>
          </FilterPopup>
        ),
      },
    ];
  };
  //  Fetches and updates  the state of preview result
  const handlePreviewResult = async () => {
    try {
      // Validate form fields before proceeding
      form
        .validateFields()
        .then(async () => {
          // Set loading state for preview result
          setLoading((prevState) => ({ ...prevState, previewResult: true }));

          // Format filter information from computed trait
          let filterInfo: FilterInfo[] = formatComputedTraitFilterInfo(
            trait.filterInfo
          );

          // Restructure filter conditions for proper formatting
          filterInfo = restructureFilterConditions(filterInfo);

          // Create a new query payload with updated filter and modifier information
          const payload = new QueryPayload({
            ...trait,
            filterInfo,
            modifierInfo: { limit: previewResult.limit },
          });
          const token = await dispatch(getToken()).unwrap();
          // Construct API request parameters
          const params = {
            app_id: appId,
            api_key: loginUser.api_key,
          };
          const payloadWithParams = {
            ...params,
            data: new QueryPayload({ ...payload,p:getPlatform(payload.filterInfo) }).toString(),
            authToken: token,
          };

          // Make API request to fetch preview results
          const data: typeof previewResult = await centralApi(
            "POST",
            API_ENDPOINTS.QUERY_RESULT_URL,
            payloadWithParams,
            params
          );

          // Update preview result state with fetched data
          setPreviewResult((prevState) => ({ ...prevState, ...data }));
        })
        .catch((errorInfo) => {
          // Handle form validation errors
          if (errorInfo.errorFields.length > 0) {
            // Extract first invalid field
            const firstInvalidField = errorInfo.errorFields[0]?.name;

            // Reset preview result state to clear previous data
            setPreviewResult((prevState) => ({
              ...prevState,
              columns: [],
              data: [],
            }));

            // Scroll to the first invalid field for user convenience
            form.scrollToField(firstInvalidField);
          }
        })
        .finally(() => {
          // Reset loading state after API response is received
          setLoading((prevState) => ({ ...prevState, previewResult: false }));
        });
    } catch (error) {
      // Reset preview result state in case of an error
      setPreviewResult((prevState) => ({
        ...prevState,
        columns: [],
        data: [],
      }));

      // Reset loading state to ensure UI does not remain in loading state
      setLoading((prevState) => ({ ...prevState, previewResult: false }));
    }
  };
  useEffect(() => {
    if (loginUser.api_key) {
      fetchTraitMetaData();
      dispatch(getToken());
      if (segment) {
        dispatch(
          fetchGeoFencingList({ api_key: loginUser.api_key, app_id: appId })
        );
        dispatch(
          fetchComputedTraitList({ api_key: loginUser.api_key, app_id: appId })
        );
      }

      // If the component is in edit mode (i.e., state contains an ID), fetch the trait details by ID
      if (state) {
        dispatch(
          fetchComputedTraitById({
            app_id: appId,
            api_key: loginUser.api_key,
            id: state.id,
            segment,
          })
        );
      } else {
        (async () => {
          let audid = "";
          try {
            audid = await getObjectId(appId, loginUser.api_key);
          } catch (error) {
            audid = "";
          }
          const tableName = `app_users_${appId}`;
          const operand="did"
          dispatch(
            setQueryPayloadData({
              tableInfo: { tableName,appId },
              audid,
              projectionInfo: segment
                ? [{ operand,type: OperatorTypeEnum.STRING,name:operand }]
                : [],
            })
          );
          form.setFieldValue("tableName", tableName);
          form.setFieldValue("projectionInfo", undefined);
          form.setFieldValue("name",undefined)
        })();
      }
    }
    return () => {
      //reset the state in redux on component unmount
      dispatch(resetQueryPayload());
    };
  }, [loginUser, state, segment]);

  useEffect(() => {
    // Check if the tableName property is available in the `trait` object
    if (trait.tableInfo?.tableName) {
      // Call the getColumns function to fetch column data based on the table name
      getColumns();
    }
  }, [trait.tableInfo]);
  return (
    <PageLayout
      title={`${segment ? "Audience" : "Trait"} | Create`}
      className="computed-trait-create-main-container"
      topLeftItem={
        <CustomBreadCrumb
          items={[
            {
              title: (
                <Link to={ROUTES.DASHBOARD_AUDIENCE}>{t("audienceLabel")}</Link>
              ),
            },
            ...(!segment
              ? [
                  {
                    title: (
                      <Link to={ROUTES.DASHBOARD_AUDIENCE_ALLTRAITS}>
                        {t("traitsLabel")}
                      </Link>
                    ),
                  },
                ]
              : []),
            {
              title: (
                <span>{state?.id ? t("editLabel") : t("createLabel")}</span>
              ),
            },
          ]}
        />
      }
    >
      {traitStatus !== STATUS.LOADING ? (
        <Form
          form={form}
          onFinish={onFormSubmit}
          labelCol={{ flex: "150px" }}
          colon={false}
          labelAlign="left"
        >
          <CreateHeader
            type={
              segment
                ? AudienceManagementPageType.SEGMENT
                : AudienceManagementPageType.TRAIT
            }
            saveButtonProps={{
              disabled: !isFormValidForPreview || ((!trait?.sd || !trait?.ed) && segment),
              loading: loading.submit,
            }}
            onDateChange={segment?({ sd, ed }) => {
               dispatch(
                setQueryPayloadData({ sd, ed })
                      );
                        }:undefined}
            initialValues={{ name: trait?.name || undefined ,description:trait.description || undefined ,sd:trait?.sd || undefined ,ed:trait?.ed || undefined}}
            onNameChange={(e: any) =>
              dispatch(setQueryPayloadData({ name: e.target.value }))
            }
            onDescriptionChange={segment?(e: any) =>
              dispatch(setQueryPayloadData({ description: e.target.value })):undefined}
          />
          <div className="py-2 trait-create-body container m-0 p-0">
            {!segment && (
              <div className="my-2">
                <strong>{t("createTraitDescriptionMsg")}</strong>
              </div>
            )}
            <div>
              {!segment && (
                <>
                  <div className="col-12">
                    <Form.Item
                      initialValue={trait?.tableInfo?.tableName || undefined}
                      rules={[
                        {
                          validator: validateNotEmpty,
                          message: `${t("selectTableLabel")}`,
                        },
                      ]}
                      name={"tableName"}
                      className="mt-3 mb-0 "
                      label={t("selectTableLabel")}
                    >
                      <Selector
                        className="input-size-lg"
                        onChange={async (value) => {
                          await dispatch(
                            setQueryPayloadData({
                              tableInfo: { tableName: value,appId },
                              projectionInfo: [],
                              filterInfo: [],
                            })
                          );
                          form.resetFields(["projectionInfo"]);
                        }}
                        options={metaData.tableValues}
                        defaultValue={trait.tableInfo?.tableName}
                      />
                    </Form.Item>
                  </div>
                  <div className="col-12">
                    <Form.Item
                      className="mt-3 mb-0 "
                      label={t("identityColumnLabel")}
                      initialValue={trait.projectionInfo?.[0]?.operand}
                      name={"projectionInfo"}
                      rules={[
                        {
                          validator: validateNotEmpty,
                          message: `${t("selectIdentityColumnMsg")}`,
                        },
                      ]}
                    >
                      <Selector
                        loading={loading.columns}
                        disabled={loading.columns}
                        className="input-size-lg"
                        onChange={(val) => {
                          const selectedValue = metaData.identityColumns.find(
                            ({ value }: { value: string }) => val == value
                          );
                          const type = OperatorManager.getPgSqlOperatorOptions(
                            selectedValue.type
                          ).type;
                          dispatch(
                            setQueryPayloadData({
                              projectionInfo: [
                                {
                                  operand: selectedValue.value,
                                  jsonb: selectedValue.jsonb || undefined,
                                  type,
                                  name: selectedValue?.label,
                                },
                              ],
                            })
                          );
                        }}
                        options={metaData.identityColumns}
                      />
                    </Form.Item>
                  </div>
                </>
              )}
              <div className="create-trait-conditions col-12 py-2 my-3">
                <div>
                  {traitListStatus !== STATUS.LOADING ? (
                    <Steps
                      direction="vertical"
                      current={100}
                      items={stepItems()}
                    />
                  ) : (
                    <Spinner />
                  )}
                </div>
              </div>

              {isFormValidForPreview && !segment && (
                <div className="col-12  col-md-10">
                  <div>
                    <h6 className="m-0 ">{t("calculationSummaryLabel")}</h6>
                    <ComputedTrait trait={trait} />
                  </div>
                </div>
              )}
            </div>
              <AccessControl
                accessControl={{
                  features: [FeatureSettingsFlagTypes.TRAIT_PREVIEW],
                }}
              >
                <div>
                  <div className="d-flex justify-content-between align-items-end py-2 create-trait-preview-result-header-container">
                    <span>
                      {previewResult.columns.length
                        ? previewResult.data.length < previewResult.limit
                          ? t("showingAllResultsLabel")
                          : t("showingLimitResultsLabel", {
                              limit: previewResult.limit,
                            })
                        : ""}
                    </span>
                    <PrimaryButton
                      type="primary"
                      disabled={loading.previewResult || !isFormValidForPreview || ((!trait?.sd || !trait?.ed) && segment)}
                      loading={loading.previewResult}
                      onClick={handlePreviewResult}
                    >
                      {t("previewResultLabel")}
                    </PrimaryButton>
                  </div>
                  {previewResult?.columns?.length ? (
                    <CustomTable
                      loading={loading.previewResult}
                      dataSource={previewResult.data}
                      columns={previewResult.columns}
                      size="small"
                      totalEntries={previewResult.data?.length}
                      pagination={{
                        pageSize: PAGE_OPTIONS.DEFAULT_VALUE,
                        simple: PAGE_OPTIONS.PAGINATION.simple,
                      }}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </AccessControl>
          </div>
        </Form>
      ) : (
        <Spinner />
      )}
    </PageLayout>
  );
}

export default CreateTrait;

const TraitPreview = ({ condition }: { condition: FilterInfo }) => {
  const { appId } = useSelector((state: any) => state.activeApp);
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const [data, setData] = useState<{ data?: QueryPayload; loading: boolean }>({
    data: undefined,
    loading: true,
  });
  const getTraitPreview = async () => {
    try {
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        id: condition.value?.[0]
      };
      const data = await centralApi(
        "GET",
        API_ENDPOINTS.TRAIT_BY_ID_URL,
        null,
        params
      );
      setData((prevState) => ({ ...prevState, data, loading: false }));
    } catch (error) {
      setData((prevState) => ({
        ...prevState,
        data: undefined,
        loading: false,
      }));
    }
  };
  useEffect(() => {
    getTraitPreview();
  }, []);
  return data.loading ? (
    <Spinner />
  ) : data.data ? (
    <ComputedTrait trait={data.data} />
  ) : (
    <span>{t("somethingWrongLabel")}</span>
  );
};
